import Button from "@/components/common/Button";
import PageHeader from "@/components/common/PageHeader";
import SearchBar from "@/components/common/SearchBar";
import CourseCard from "@/components/dashboard/CourseCard";
import { useCourses } from "@/hooks/queries/useCourseQueries";
import { useAuth } from "@/hooks/useAuth";
import {
  AcademicCapIcon,
  AdjustmentsHorizontalIcon,
  BookOpenIcon,
  FunnelIcon,
  SparklesIcon,
} from "@heroicons/react/24/outline";
import { AnimatePresence, motion } from "framer-motion";
import Lottie from "lottie-react";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

const Courses = () => {
  const { t, i18n } = useTranslation();
  console.log("[Courses] Current language:", i18n.language);
  const { user } = useAuth();
  const { data: courses = [], isLoading, error } = useCourses();
  const [searchQuery, setSearchQuery] = useState("");
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [selectedDifficulty, setSelectedDifficulty] = useState("all");
  const [selectedTab, setSelectedTab] = useState(0);
  const [oopsAnimation, setOopsAnimation] = useState<any>(null);
  const [activeFilter, setActiveFilter] = useState<string>("all");

  // Get user's learning progress to show course status
  const getUserCourseProgress = (courseId: string) => {
    if (!user?.learningProgress) return null;
    
    const courseProgress = user.learningProgress.find(
      (course: any) => course.courseId === courseId
    );
    
    if (!courseProgress) return null;
    
    return {
      progress: courseProgress.progress || 0,
      isCompleted: Boolean(courseProgress.progress === 100 || courseProgress.completedAt),
      isInProgress: courseProgress.progress > 0 && courseProgress.progress < 100,
      startedAt: courseProgress.startedAt,
      completedAt: courseProgress.completedAt,
      quizScores: courseProgress.quizzes || [],
      averageQuizScore: courseProgress.quizzes && courseProgress.quizzes.length > 0 
        ? Math.round(courseProgress.quizzes.reduce((sum: number, quiz: any) => sum + quiz.score, 0) / courseProgress.quizzes.length)
        : 0
    };
  };

  useEffect(() => {
    const oopsPath = `${import.meta.env.BASE_URL || "/"}Oops.json`;
    fetch(oopsPath)
      .then((res) => res.json())
      .then(setOopsAnimation)
      .catch(() => setOopsAnimation(null));
  }, []);

  // Language-aware course filtering
  const language = i18n.language;
  const getCourseTitle = (course: any) => {
    if (language === "am") return course.title?.am || course.title?.en || "";
    if (language === "en") return course.title?.en || course.title?.am || "";
    // Default to English for other languages
    return course.title?.en || course.title?.am || "";
  };
  const getCourseDescription = (course: any) => {
    if (language === "am")
      return course.courseDescription?.am || course.courseDescription?.en || "";
    if (language === "en")
      return course.courseDescription?.en || course.courseDescription?.am || "";
    // Default to English for other languages
    return course.courseDescription?.en || course.courseDescription?.am || "";
  };

  // Filter and sort courses based on search query and filters using useMemo
  const filteredCourses = useMemo(() => {
    if (!Array.isArray(courses)) return [];
    
    const filtered = courses.filter((course) => {
      const title = getCourseTitle(course).toLowerCase();
      const desc = getCourseDescription(course).toLowerCase();
      const matchesSearch =
        title.includes(searchQuery.toLowerCase()) ||
        desc.includes(searchQuery.toLowerCase());
      
      // Use activeFilter for card-based filtering, fallback to selectedDifficulty
      const currentFilter = activeFilter !== "all" ? activeFilter : selectedDifficulty;
      const matchesDifficulty =
        currentFilter === "all" ||
        (course.courseLevel &&
          course.courseLevel.toLowerCase() === currentFilter.toLowerCase());
      
      return matchesSearch && matchesDifficulty;
    });

    // Sort courses: In Progress first, then Completed, then Not Started
    return filtered.sort((a, b) => {
      const progressA = getUserCourseProgress(a._id);
      const progressB = getUserCourseProgress(b._id);
      
      // Priority order: In Progress (1), Completed (2), Not Started (3)
      const getPriority = (progress: any) => {
        if (!progress) return 3; // Not Started
        if (progress.isInProgress) return 1; // In Progress
        if (progress.isCompleted) return 2; // Completed
        return 3; // Not Started (fallback)
      };
      
      const priorityA = getPriority(progressA);
      const priorityB = getPriority(progressB);
      
      // If different priorities, sort by priority
      if (priorityA !== priorityB) {
        return priorityA - priorityB;
      }
      
      // If same priority, sort alphabetically by title
      const titleA = getCourseTitle(a).toLowerCase();
      const titleB = getCourseTitle(b).toLowerCase();
      return titleA.localeCompare(titleB);
    });
  }, [courses, searchQuery, language, selectedDifficulty, activeFilter, user]);

  // Debug: log the raw and filtered courses
  console.log("courses:", courses);
  console.log("filteredCourses:", filteredCourses);

  const handleSearch = (query: string) => {
    setSearchQuery(query);
  };

  const resetFilters = () => {
    setSelectedCategory("all");
    setSelectedDifficulty("all");
    setSearchQuery("");
    setActiveFilter("all");
  };

  // Handle card filter clicks
  const handleCardFilter = (filterType: string) => {
    setActiveFilter(filterType);
    setSelectedDifficulty(filterType);
    setSearchQuery(""); // Clear search when using card filters
  };

  // Get course statistics
  const courseStats = useMemo(() => {
    const total = courses.length;
    const beginner = courses.filter(
      (c) => c.courseLevel?.toLowerCase() === "beginner"
    ).length;
    const intermediate = courses.filter(
      (c) => c.courseLevel?.toLowerCase() === "intermediate"
    ).length;
    const advanced = courses.filter(
      (c) => c.courseLevel?.toLowerCase() === "advanced"
    ).length;

    return { total, beginner, intermediate, advanced };
  }, [courses]);

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 via-primary-50 to-primary-100 dark:from-gray-900 dark:via-primary-900/20 dark:to-primary-800/20">
        <div className="flex items-center justify-center min-h-screen">
          <div className="text-center">
            <div className="mx-auto h-12 w-12 animate-spin rounded-full border-4 border-primary-500 border-t-transparent mb-4"></div>
            <p className="text-lg font-medium text-gray-700 dark:text-gray-300">
              {t("common.loading")}
            </p>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-2">
              {t("courses.loadingLearningJourney")}
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 via-primary-50 to-primary-100 dark:from-gray-900 dark:via-primary-900/20 dark:to-primary-800/20">
        <div className="flex items-center justify-center min-h-screen">
          <div className="text-center max-w-md mx-auto px-4">
            <div className="mx-auto w-40 h-40 sm:w-48 sm:h-48 md:w-56 md:h-56 lg:w-64 lg:h-64 text-gray-400 dark:text-gray-500">
              {oopsAnimation && (
                <Lottie
                  animationData={oopsAnimation}
                  loop={true}
                  autoplay={true}
                />
              )}
            </div>
            <h3 className="mt-6 text-2xl md:text-3xl lg:text-4xl font-bold text-gray-900 dark:text-gray-100">
              {t("common.oops")}
            </h3>
            <p className="mt-4 text-lg md:text-xl lg:text-2xl text-gray-500 dark:text-gray-400">
              {t("courses.errorLoading")}
            </p>
            <Button
              variant="primary"
              className="mt-6"
              onClick={() => window.location.reload()}
            >
              {t("courses.tryAgain")}
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // Show a friendly message if there are no courses and no error
  if (!isLoading && !error && courses.length === 0) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 via-primary-50 to-primary-100 dark:from-gray-900 dark:via-primary-900/20 dark:to-primary-800/20">
        <div className="flex items-center justify-center min-h-screen">
          <div className="text-center max-w-md mx-auto px-4">
            <div className="mx-auto h-24 w-24 text-gray-400 dark:text-gray-500 mb-6">
              <BookOpenIcon className="h-full w-full" />
            </div>
            <h3 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-4">
              {t("courses.noCoursesAskAdmin")}
            </h3>
            <p className="text-gray-600 dark:text-gray-400 mb-6">
              {t("courses.noCoursesAvailableMessage")}
            </p>
            <div className="bg-gradient-to-r from-primary-50 to-primary-100 dark:from-primary-900/20 dark:to-primary-800/20 rounded-xl p-6 border border-primary-200 dark:border-primary-800">
              <AcademicCapIcon className="w-8 h-8 text-primary-500 mx-auto mb-3" />
              <p className="text-sm text-primary-700 dark:text-primary-300">
                {t("courses.coursesWillAppearMessage")}
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-primary-50 to-primary-100 dark:from-gray-900 dark:via-primary-900/20 dark:to-primary-800/20">
      <div className="container mx-auto px-2 sm:px-4 py-4 sm:py-8">
        {/* Header Section */}
        <PageHeader
          title={t("courses.allCourses")}
          description={t("courses.browseDescription")}
        />

        {/* Course Statistics */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="mb-6 sm:mb-8"
        >
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-6">
            {/* Total Courses Card */}
            <div 
              className={`bg-gradient-to-br from-primary-500 to-primary-600 rounded-xl sm:rounded-2xl p-4 sm:p-6 text-white shadow-lg cursor-pointer transition-all duration-300 hover:shadow-xl hover:scale-105 ${
                activeFilter === "all" ? "ring-2 sm:ring-4 ring-primary-300 ring-opacity-50 shadow-2xl scale-105" : ""
              }`}
              onClick={() => handleCardFilter("all")}
            >
              <div className="flex flex-col sm:flex-row items-start sm:items-center sm:justify-between gap-2">
                <div className="min-w-0">
                  <p className="text-primary-100 text-xs sm:text-sm font-medium truncate">
                    {t("courses.totalCourses")}
                  </p>
                  <p className="text-2xl sm:text-3xl font-bold">{courseStats.total}</p>
                </div>
                <BookOpenIcon className="w-6 h-6 sm:w-8 sm:h-8 text-primary-200 flex-shrink-0" />
              </div>
            </div>

            {/* Beginner Card */}
            <div 
              className={`bg-gradient-to-br from-primary-400 to-primary-500 rounded-xl sm:rounded-2xl p-4 sm:p-6 text-white shadow-lg cursor-pointer transition-all duration-300 hover:shadow-xl hover:scale-105 ${
                activeFilter === "beginner" ? "ring-2 sm:ring-4 ring-primary-300 ring-opacity-50 shadow-2xl scale-105" : ""
              }`}
              onClick={() => handleCardFilter("beginner")}
            >
              <div className="flex flex-col sm:flex-row items-start sm:items-center sm:justify-between gap-2">
                <div className="min-w-0">
                  <p className="text-primary-100 text-xs sm:text-sm font-medium truncate">{t("courses.beginner")}</p>
                  <p className="text-2xl sm:text-3xl font-bold">{courseStats.beginner}</p>
                </div>
                <SparklesIcon className="w-6 h-6 sm:w-8 sm:h-8 text-primary-200 flex-shrink-0" />
              </div>
            </div>

            {/* Intermediate Card */}
            <div 
              className={`bg-gradient-to-br from-primary-300 to-primary-400 rounded-xl sm:rounded-2xl p-4 sm:p-6 text-white shadow-lg cursor-pointer transition-all duration-300 hover:shadow-xl hover:scale-105 ${
                activeFilter === "intermediate" ? "ring-2 sm:ring-4 ring-primary-300 ring-opacity-50 shadow-2xl scale-105" : ""
              }`}
              onClick={() => handleCardFilter("intermediate")}
            >
              <div className="flex flex-col sm:flex-row items-start sm:items-center sm:justify-between gap-2">
                <div className="min-w-0">
                  <p className="text-primary-100 text-xs sm:text-sm font-medium truncate">
                    {t("courses.intermediate")}
                  </p>
                  <p className="text-2xl sm:text-3xl font-bold">
                    {courseStats.intermediate}
                  </p>
                </div>
                <AcademicCapIcon className="w-6 h-6 sm:w-8 sm:h-8 text-primary-200 flex-shrink-0" />
              </div>
            </div>

            {/* Advanced Card */}
            <div 
              className={`bg-gradient-to-br from-primary-600 to-primary-700 rounded-xl sm:rounded-2xl p-4 sm:p-6 text-white shadow-lg cursor-pointer transition-all duration-300 hover:shadow-xl hover:scale-105 ${
                activeFilter === "advanced" ? "ring-2 sm:ring-4 ring-primary-300 ring-opacity-50 shadow-2xl scale-105" : ""
              }`}
              onClick={() => handleCardFilter("advanced")}
            >
              <div className="flex flex-col sm:flex-row items-start sm:items-center sm:justify-between gap-2">
                <div className="min-w-0">
                  <p className="text-primary-100 text-xs sm:text-sm font-medium truncate">
                    {t("courses.advanced")}
                  </p>
                  <p className="text-2xl sm:text-3xl font-bold">{courseStats.advanced}</p>
                </div>
                <SparklesIcon className="w-6 h-6 sm:w-8 sm:h-8 text-primary-200 flex-shrink-0" />
              </div>
            </div>
          </div>
        </motion.div>

        {/* Search and Filter Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="mb-6 sm:mb-8"
        >
          <div className="bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm rounded-xl sm:rounded-2xl shadow-xl p-4 sm:p-6 border border-gray-200/50 dark:border-gray-700/50">
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between space-y-4 lg:space-y-0 lg:space-x-6">
              <div className="flex-1">
                <SearchBar
                  placeholder={t("courses.searchPlaceholder")}
                  onSearch={handleSearch}
                  className="w-full"
                />
              </div>

              <div className="flex items-center space-x-2 sm:space-x-3">
                {(searchQuery || activeFilter !== "all") && (
                  <Button
                    variant="outline"
                    onClick={resetFilters}
                    className="text-primary-600 border-primary-300 hover:bg-primary-50 dark:text-primary-400 dark:border-primary-600 dark:hover:bg-primary-900/20 text-xs sm:text-sm px-3 sm:px-4 whitespace-nowrap"
                  >
                    {t("courses.clearAll")}
                  </Button>
                )}
              </div>
            </div>

            {/* Active Filters Display */}
            {(searchQuery || activeFilter !== "all") && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: "auto" }}
                exit={{ opacity: 0, height: 0 }}
                className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700"
              >
                <div className="flex flex-wrap items-center gap-2">
                  <span className="text-sm text-gray-600 dark:text-gray-400">
                    {t("courses.activeFilters")}
                  </span>
                  {searchQuery && (
                    <span className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-primary-100 text-primary-800 dark:bg-primary-900/30 dark:text-primary-300">
                      {t("courses.searchLabel")} "{searchQuery}"
                    </span>
                  )}
                  {activeFilter !== "all" && (
                    <span className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-primary-100 text-primary-800 dark:bg-primary-900/30 dark:text-primary-300">
                      {t("courses.levelLabel")} {activeFilter}
                    </span>
                  )}
                </div>
              </motion.div>
            )}
          </div>
        </motion.div>

        {/* Filter Panel */}
        <AnimatePresence>
          {isFilterOpen && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.3 }}
              className="mb-8 overflow-hidden"
            >
              <div className="bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm rounded-2xl shadow-xl border border-gray-200/50 dark:border-gray-700/50">
                <div className="p-6">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                    {t("courses.filterCourses")}
                  </h3>
                  <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">
                        {t("courses.filterBy")} {t("courses.difficulty.all")}
                      </label>
                      <select
                        value={selectedDifficulty}
                        onChange={(e) => setSelectedDifficulty(e.target.value)}
                        className="w-full rounded-xl border border-gray-300 bg-white py-3 px-4 shadow-sm focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500/20 dark:border-gray-600 dark:bg-gray-700 dark:text-white transition-all duration-200"
                      >
                        <option value="all">
                          {t("courses.difficulty.all")}
                        </option>
                        <option value="beginner">
                          {t("courses.difficulty.beginner")}
                        </option>
                        <option value="intermediate">
                          {t("courses.difficulty.intermediate")}
                        </option>
                        <option value="advanced">
                          {t("courses.difficulty.advanced")}
                        </option>
                      </select>
                    </div>

                    <div className="flex items-end">
                      <Button
                        variant="outline"
                        onClick={resetFilters}
                        className="w-full bg-gradient-to-r from-gray-50 to-gray-100 dark:from-gray-700 dark:to-gray-800 hover:from-gray-100 hover:to-gray-200 dark:hover:from-gray-600 dark:hover:to-gray-700"
                      >
                        {t("common.resetFilters")}
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Course Grid */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.4 }}
        >
          {filteredCourses.length > 0 ? (
            <div className="grid gap-4 sm:gap-6 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3">
              {filteredCourses.map((course, index) => (
                <motion.div
                  key={course._id}
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  whileHover={{
                    scale: 1.02,
                    transition: { duration: 0.2 },
                  }}
                >
                  <CourseCard
                    {...course}
                    title={getCourseTitle(course)}
                    courseDescription={getCourseDescription(course)}
                    userProgress={getUserCourseProgress(course._id)}
                  />
                </motion.div>
              ))}
            </div>
          ) : (
            <div className="bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm rounded-xl sm:rounded-2xl shadow-xl border border-gray-200/50 dark:border-gray-700/50 p-6 sm:p-12 text-center">
              <div className="mx-auto h-24 w-24 text-gray-400 dark:text-gray-500 mb-6">
                <FunnelIcon className="h-full w-full" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-3">
                {t("courses.noCourses")}
              </h3>
              <p className="text-gray-600 dark:text-gray-400 mb-6 max-w-md mx-auto">
                {t("courses.tryDifferentFilters")}
              </p>
              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                <Button
                  variant="primary"
                  onClick={resetFilters}
                  className="bg-gradient-to-r from-primary-500 to-primary-600 hover:from-primary-600 hover:to-primary-700"
                >
                  {t("common.resetFilters")}
                </Button>
                <Button variant="outline" onClick={() => setSearchQuery("")}>
                  {t("courses.clearSearch")}
                </Button>
              </div>
            </div>
          )}
        </motion.div>

        {/* Results Summary */}
        {filteredCourses.length > 0 && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.6, delay: 0.6 }}
            className="mt-8 text-center"
          >
            <div className="inline-flex items-center px-4 py-2 bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm rounded-full shadow-lg border border-gray-200/50 dark:border-gray-700/50">
              <SparklesIcon className="w-4 h-4 text-primary-500 mr-2" />
              <span className="text-sm text-gray-600 dark:text-gray-400">
                {t("courses.showing")}{" "}
                <span className="font-semibold text-gray-900 dark:text-white">
                  {filteredCourses.length}
                </span>{" "}
                {t("courses.of")}{" "}
                <span className="font-semibold text-gray-900 dark:text-white">
                  {courses.length}
                </span>{" "}
                {t("courses.courses")}
              </span>
            </div>
          </motion.div>
        )}
      </div>
    </div>
  );
};

export default Courses;
